import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import './App.css';

// Components
import Navigation from './components/Navigation';
import HeroSection from './components/HeroSection';
import Features from './components/Features';
import StyleGallery from './components/StyleGallery';
import HowItWorks from './components/HowItWorks';
import Pricing from './components/Pricing';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';

// Pages
import About from './pages/About';
import Press from './pages/Press';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Refund from './pages/Refund';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import EULA from './pages/EULA';
import Cookies from './pages/Cookies';
import GDPR from './pages/GDPR';
import AIDisclosure from './pages/AIDisclosure';
import DataDeletion from './pages/DataDeletion';
import Support from './pages/Support';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Scroll to hash on navigation
const ScrollToHash = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return null;
};

// Home page component
const HomePage = () => {
  useEffect(() => {
    // Check for hash on initial load
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);
  
  return (
    <>
      <Navigation />
      <HeroSection />
      <Features />
      <StyleGallery />
      <HowItWorks />
      <Pricing />
      <Testimonials />
      <Footer />
    </>
  );
};

// Layout component for subpages
const PageLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    <nav className="nav-header">
      <Link to="/" className="nav-logo">GET AI PORTRAIT</Link>
      <div className="nav-links">
        <Link to="/#features">Features</Link>
        <Link to="/#style-gallery">Styles</Link>
        <Link to="/#pricing">Pricing</Link>
        <Link to="/contact">Contact</Link>
      </div>
    </nav>
    {children}
    <Footer />
  </>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <ScrollToHash />
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<PageLayout><About /></PageLayout>} />
          <Route path="/press" element={<PageLayout><Press /></PageLayout>} />
          <Route path="/support" element={<PageLayout><Support /></PageLayout>} />
          <Route path="/faq" element={<PageLayout><FAQ /></PageLayout>} />
          <Route path="/contact" element={<PageLayout><Contact /></PageLayout>} />
          <Route path="/refund-policy" element={<PageLayout><Refund /></PageLayout>} />
          <Route path="/privacy" element={<PageLayout><Privacy /></PageLayout>} />
          <Route path="/terms" element={<PageLayout><Terms /></PageLayout>} />
          <Route path="/eula" element={<PageLayout><EULA /></PageLayout>} />
          <Route path="/cookies" element={<PageLayout><Cookies /></PageLayout>} />
          <Route path="/gdpr" element={<PageLayout><GDPR /></PageLayout>} />
          <Route path="/ai-disclosure" element={<PageLayout><AIDisclosure /></PageLayout>} />
          <Route path="/data-deletion" element={<PageLayout><DataDeletion /></PageLayout>} />
          {/* Legacy routes for backwards compatibility */}
          <Route path="/help" element={<PageLayout><Support /></PageLayout>} />
          <Route path="/refund" element={<PageLayout><Refund /></PageLayout>} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;